import React from 'react'
import styled from 'styled-components'

import type { PropertyMatch as PropertyMatchProps } from '@/api/types/matchCriteria'
import { ReactComponent as ActivityIcon } from '@/assets/svgs/activity.svg'
import { useNanoId } from '@/hooks'
import { Divider } from '@/UIkit/Divider'
import { IconGenerator } from '@/UIkit/IconGenerator'

import {
  MATCH_CRITERIA_ICON,
  MATCH_CRITERIA_BAND,
  MATCH_CRITERIA_STATUS,
} from './constants'
import { PropertyMatchKey } from './PropertyMatchKey'
import * as S from './styles'

export const Txt = styled.div<{
  $fs?: string
  $weight?: string
  $fontFamily?: string
  $lineHeight?: number
}>`
  font-size: ${({ $fs }) => $fs};
  font-weight: ${({ $weight }) => $weight || 'normal'};
  font-family: ${({ $fontFamily }) => $fontFamily || 'inherit'};
  line-height: ${({ $lineHeight }) => $lineHeight || '18px'};
`

export const PropertyMatch = ({
  propertyMatch,
}: {
  propertyMatch: PropertyMatchProps
}) => {
  const id = useNanoId()
  const { band, criteria, keyInsights, totalScore } = propertyMatch

  return (
    <>
      <S.MatchBand data-tooltip-id={id} status={band}>
        <IconGenerator iconComponent={ActivityIcon} width={16} height={16} />
        <Txt $fs="14" $weight="medium">
          {MATCH_CRITERIA_BAND[band]}
        </Txt>
        <Txt $fs="14" $fontFamily="itcGothicPro" $weight="bold">
          &nbsp;{totalScore}%
        </Txt>
      </S.MatchBand>
      <S.MatchContent id={id} place="bottom">
        <S.Header>
          <Txt $fs="14" $fontFamily="itcGothicPro" $weight="semiBold">
            Request Match
          </Txt>
          <S.ScoreTag
            status={band}
            $radius="full"
            data-testid="match-score-tag"
          >
            <Txt $fontFamily="itcGothicPro" $fs="14" $weight="bold">
              {totalScore}%
            </Txt>
          </S.ScoreTag>
        </S.Header>

        {criteria.length > 0 && (
          <ul>
            {criteria.map(({ type, label, status }) => (
              <li key={type}>
                <S.CriteriaWrapper>
                  <S.IconWrapper>
                    <IconGenerator
                      iconComponent={MATCH_CRITERIA_ICON[type]}
                      width={16}
                      height={16}
                      viewBox="0 0 20 20"
                    />
                  </S.IconWrapper>
                  <Txt $fs="14" $lineHeight={1.2}>
                    {label}
                  </Txt>
                </S.CriteriaWrapper>
                <S.StatusWrapper status={status}>
                  <IconGenerator
                    iconComponent={MATCH_CRITERIA_STATUS[status]}
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                  />
                </S.StatusWrapper>
              </li>
            ))}
          </ul>
        )}

        {keyInsights.length > 0 && (
          <div>
            <Divider $bg="cautiousBlue" $mb="4" />
            <Txt $fs="14" $fontFamily="itcGothicPro" $weight="semiBold">
              Key Insights
            </Txt>
            <S.KeysWrapper>
              {keyInsights.map((item) => (
                <PropertyMatchKey
                  key={item.type}
                  {...item}
                  isHighlight={false}
                />
              ))}
            </S.KeysWrapper>
          </div>
        )}
      </S.MatchContent>
    </>
  )
}
